import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const AuditLogModal = ({ onClose }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all');

  const actionTypes = [
    { value: 'all', label: 'All Actions' },
    { value: 'permission_granted', label: 'Permission Granted' },
    { value: 'permission_revoked', label: 'Permission Revoked' },
    { value: 'role_created', label: 'Role Created' },
    { value: 'role_updated', label: 'Role Updated' },
    { value: 'role_deleted', label: 'Role Deleted' },
    { value: 'personnel_role_changed', label: 'Personnel Role Changed' }
  ];

  useEffect(() => {
    fetchAuditLogs();
  }, [filterType]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('permission_audit_log')?.select('*')?.order('created_at', { ascending: false })?.limit(100);

      if (filterType !== 'all') {
        query = query?.eq('action_type', filterType);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setAuditLogs(data || []);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'permission_granted':
        return { name: 'CheckCircle', color: 'var(--color-primary)' };
      case 'permission_revoked':
        return { name: 'XCircle', color: 'var(--color-destructive)' };
      case 'role_created':
        return { name: 'PlusCircle', color: 'var(--color-primary)' };
      case 'role_updated':
        return { name: 'Edit', color: 'var(--color-foreground)' };
      case 'role_deleted':
        return { name: 'Trash2', color: 'var(--color-destructive)' };
      case 'personnel_role_changed':
        return { name: 'RefreshCw', color: 'var(--color-foreground)' };
      default:
        return { name: 'Activity', color: 'var(--color-muted-foreground)' };
    }
  };

  const formatActionType = (actionType) => {
    return actionType
      ?.split('_')
      ?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
      ?.join(' ');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1400] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Permission Audit Log
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Track all permission and role changes
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-4 md:p-6">
          {/* Filter */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Filter by Action Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {actionTypes?.map((type) => (
                <option key={type?.value} value={type?.value}>
                  {type?.label}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : auditLogs?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground">No audit logs found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {auditLogs?.map((log) => {
                const actionIcon = getActionIcon(log?.action_type);
                return (
                  <div
                    key={log?.id}
                    className="bg-background border border-border rounded-lg p-4 hover:border-primary/50 transition-smooth"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <Icon name={actionIcon?.name} size={20} color={actionIcon?.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div>
                            <h3 className="text-sm font-semibold text-foreground">
                              {formatActionType(log?.action_type)}
                            </h3>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDate(log?.created_at)}
                            </p>
                          </div>
                          {log?.role && (
                            <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded">
                              {log?.role}
                            </span>
                          )}
                        </div>
                        {log?.description && (
                          <p className="text-sm text-foreground mb-2">{log?.description}</p>
                        )}
                        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                          {log?.permission && (
                            <span>
                              <strong>Permission:</strong> {log?.permission}
                            </span>
                          )}
                          {log?.changed_by_name && (
                            <span>
                              <strong>Changed By:</strong> {log?.changed_by_name}
                            </span>
                          )}
                          {log?.old_value && log?.new_value && (
                            <span>
                              <strong>Change:</strong> {log?.old_value} → {log?.new_value}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex gap-3 pt-6 border-t border-border mt-6">
            <Button variant="default" onClick={onClose} fullWidth>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogModal;